<template>
  <div class="relative min-h-screen bg-gray-50 dark:bg-gray-900 lg:flex">
    <!-- Sidebar -->
    <Sidebar
      :is-open="isSidebarOpen"
      :show-overlay="isMobile"
      :show-close-button="false"
      :user="user"
      @close="isSidebarOpen = false"
      @logout="handleSignOut"
    />

    <!-- Main content -->
    <div
      class="flex-1 flex flex-col transition-all duration-300 ease-in-out"
      :class="{ 'lg:ml-64': isSidebarOpen, 'lg:ml-0': !isSidebarOpen }"
    >
      <!-- Header with branding -->
      <header
        class="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm"
      >
        <div class="px-4 sm:px-6 lg:px-8">
          <div class="flex justify-between items-center h-20">
            <!-- Sidebar toggle button (visible on all screen sizes) -->
            <button
              @click="isSidebarOpen = !isSidebarOpen"
              class="p-2 rounded-lg text-gray-400 dark:text-gray-300 hover:text-gray-600 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200"
              title="Toggle sidebar"
            >
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  v-if="isSidebarOpen"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M11 19l-7-7 7-7m8 14l-7-7 7-7"
                />
                <path
                  v-else
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            </button>

            <!-- Company branding -->
            <div class="flex-1 lg:flex lg:items-center lg:justify-between">
              <div class="min-w-0 flex-1 ml-6">
                <div class="flex items-center space-x-4">
                  <div
                    class="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg"
                  >
                    <SharedIcon :size="28" gradient-id="gradient-header" />
                  </div>
                  <div>
                    <span class="text-xl font-bold text-gray-900 dark:text-white">ASAB</span>
                    <p class="text-sm text-gray-500 dark:text-gray-400">Neural Platform</p>
                  </div>
                </div>
              </div>

              <!-- Navigation links -->
              <div class="hidden lg:flex lg:items-center lg:space-x-8">
                <router-link
                  to="/app"
                  class="text-gray-700 dark:text-gray-200 hover:text-blue-600 dark:hover:text-blue-400 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200"
                  :class="{
                    'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20':
                      route.path === '/app/dashboard',
                  }"
                >
                  Dashboard
                </router-link>
                <router-link
                  to="/app/api-keys"
                  class="text-gray-700 dark:text-gray-200 hover:text-blue-600 dark:hover:text-blue-400 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200"
                  :class="{
                    'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20':
                      route.path === '/app/api-keys',
                  }"
                >
                  API Keys
                </router-link>
                <router-link
                  to="/app/organizations"
                  class="text-gray-700 dark:text-gray-200 hover:text-blue-600 dark:hover:text-blue-400 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 relative"
                  :class="{
                    'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20':
                      route.path.startsWith('/app/organizations'),
                  }"
                >
                  Organizations
                  <span
                    v-if="pendingInvitationsCount > 0"
                    class="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white ring-2 ring-white dark:ring-gray-800"
                  >
                    {{ pendingInvitationsCount }}
                  </span>
                </router-link>
                <router-link
                  to="/app/documentation"
                  class="text-gray-700 dark:text-gray-200 hover:text-blue-600 dark:hover:text-blue-400 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200"
                  :class="{
                    'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20':
                      route.path === '/app/documentation',
                  }"
                >
                  Documentation
                </router-link>
              </div>
            </div>

            <!-- Header actions -->
            <div class="flex items-center space-x-4">
              <!-- Theme toggle -->
              <button
                @click="toggleTheme"
                class="p-2 rounded-md text-gray-400 dark:text-gray-300 hover:text-gray-600 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                title="Toggle theme"
              >
                <svg
                  v-if="isDark"
                  class="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
                  />
                </svg>
                <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
                  />
                </svg>
              </button>

              <!-- Organization switcher -->
              <OrganizationSwitcher @create="showCreateOrgModal = true" />

              <!-- User menu -->
              <UserMenu
                v-if="user"
                :user="user"
                :is-authenticated="isAuthenticated"
                :show-user-info="true"
                :menu-items="userMenuItems"
                position="right"
                @logout="handleSignOut"
              />
            </div>
          </div>
        </div>
      </header>

      <!-- Page Content -->
      <main class="flex-1 bg-gray-50 dark:bg-gray-900">
        <div class="py-6 px-4 sm:px-6 lg:px-8">
          <!-- Per-feature: keyed by route, so one view's crash is contained to
               the content area and navigating away gives a clean mount. -->
          <ErrorBoundary
            :key="route.name as string"
            :name="currentFeatureName"
            :show-details="isDev"
          >
            <router-view />
          </ErrorBoundary>
        </div>
      </main>

      <!-- Footer -->
      <Footer />
    </div>

    <!-- Modals -->
    <CreateOrganizationModal v-model:open="showCreateOrgModal" />
  </div>
</template>

<script setup lang="ts">
  import { ref, watch, onMounted, onBeforeUnmount, computed } from 'vue';
  import { useRouter, useRoute } from 'vue-router';
  import { ErrorBoundary, Footer, Sidebar, SharedIcon, UserMenu } from '@asabdev/asab-ui';
  import { useAuth } from '@/features/auth/composables/useAuth';
  import {
    OrganizationSwitcher,
    CreateOrganizationModal,
  } from '@/features/organizations/components';
  import { useOrganizationsStore } from '@/stores/organizations';
  import { storeToRefs } from 'pinia';

  // State
  const isSidebarOpen = ref(true);
  const isDark = ref(false); // Default to light mode, will be overridden by saved preference
  const showCreateOrgModal = ref(false);
  const windowWidth = ref(window.innerWidth);
  const router = useRouter();
  const route = useRoute();

  // Routes already carry a human name in meta.title ('Agents', 'Billing').
  // Reuse it rather than inventing a second naming scheme.
  const currentFeatureName = computed(() => (route.meta?.title as string) || '');

  // Resolved here rather than in asab-ui: Vite freezes import.meta.env at the
  // library's own build, so a library reading it reports its build mode, not ours.
  const isDev = import.meta.env.DEV;
  const { user, isAuthenticated, logout } = useAuth();
  const orgStore = useOrganizationsStore();
  const { pendingInvitationsCount } = storeToRefs(orgStore);

  // Computed
  const isMobile = computed(() => {
    return windowWidth.value < 1024;
  });

  const userMenuItems = computed(() => [
    {
      name: 'Profile',
      route: '/app/profile',
      icon: 'user',
    },
  ]);

  // Theme management functions
  const getInitialTheme = () => {
    // Check localStorage first (same format as main.ts)
    const savedPreferences = localStorage.getItem('userPreferences');
    if (savedPreferences) {
      try {
        const preferences = JSON.parse(savedPreferences);
        if (preferences.theme) {
          return preferences.theme === 'dark';
        }
      } catch {
        console.warn('Failed to parse saved theme preference');
      }
    }

    // Fallback to system preference
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  };

  const applyTheme = (dark: boolean) => {
    const theme = dark ? 'dark' : 'light';

    // Apply to document
    if (dark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }

    // Save to localStorage in the same format as main.ts
    try {
      const existingPreferences = localStorage.getItem('userPreferences');
      let preferences: { theme?: string } = {};

      if (existingPreferences) {
        preferences = JSON.parse(existingPreferences);
      }

      preferences.theme = theme;
      localStorage.setItem('userPreferences', JSON.stringify(preferences));
    } catch {
      console.warn('Failed to save theme preference');
    }
  };

  // Methods
  const handleSignOut = async () => {
    await logout();
    isSidebarOpen.value = false; // Close sidebar on logout
    router.push('/');
  };

  const toggleTheme = () => {
    isDark.value = !isDark.value;
    applyTheme(isDark.value);
  };

  const updateSidebarVisibility = () => {
    windowWidth.value = window.innerWidth;
    // Only auto-close on mobile, don't auto-open on desktop
    if (window.innerWidth < 1024) {
      isSidebarOpen.value = false;
    }
    // On desktop, let user control sidebar state manually
  };

  // Hooks
  onMounted(() => {
    // Initialize theme from saved preference or system preference
    isDark.value = getInitialTheme();
    applyTheme(isDark.value);

    updateSidebarVisibility();
    window.addEventListener('resize', updateSidebarVisibility);

    // Listen for system theme changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleSystemThemeChange = (e: MediaQueryListEvent) => {
      // Only auto-update if user hasn't set a preference
      const savedPreferences = localStorage.getItem('userPreferences');
      let hasUserThemePreference = false;

      if (savedPreferences) {
        try {
          const preferences = JSON.parse(savedPreferences);
          hasUserThemePreference = !!preferences.theme;
        } catch {
          // If parsing fails, assume no preference
          hasUserThemePreference = false;
        }
      }

      if (!hasUserThemePreference) {
        isDark.value = e.matches;
        applyTheme(isDark.value);
      }
    };

    mediaQuery.addEventListener('change', handleSystemThemeChange);

    // Cleanup function will be called in onBeforeUnmount
    return () => {
      mediaQuery.removeEventListener('change', handleSystemThemeChange);
    };
  });

  onBeforeUnmount(() => {
    window.removeEventListener('resize', updateSidebarVisibility);
  });

  // Watch for route changes to close sidebar on mobile
  watch(
    () => router.currentRoute.value,
    () => {
      if (window.innerWidth < 1024) {
        isSidebarOpen.value = false;
      }
    }
  );
</script>
